import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const LightingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="ljusdesign"
      data-testid="lighting-section"
      ref={ref}
      className="relative py-32 md:py-48 overflow-hidden"
    >
      {/* Background Image with Parallax Effect */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/19zia0io_20220915_164121.jpg')`,
          }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[#050505]/75" />
        {/* Gold Gradient Glow */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#D4AF37]/5 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 md:px-12 text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-8"
        >
          Signatur
        </motion.p>

        <motion.h2
          data-testid="lighting-title"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-manrope font-extralight text-white tracking-tight mb-8"
        >
          Ljus förändrar
          <br />
          <span className="text-[#D4AF37]">allt.</span>
        </motion.h2>

        <motion.p
          data-testid="lighting-description"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl mx-auto text-zinc-400 text-base md:text-lg leading-relaxed mb-12"
        >
          Vi kombinerar teknisk precision med estetisk känsla för att skapa 
          ljusmiljöer som förvandlar rum. Från diskret inbyggd belysning till 
          dramatiska ljusinstallationer – vi ger ditt hem en unik karaktär.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-wrap justify-center gap-8 md:gap-16 mb-12"
        >
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-manrope font-light text-white">LED-lister</div>
            <div className="text-sm text-zinc-500 uppercase tracking-wider mt-1">Infällda</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-manrope font-light text-white">Spotlights</div>
            <div className="text-sm text-zinc-500 uppercase tracking-wider mt-1">Dimbar</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-manrope font-light text-white">Smart Home</div>
            <div className="text-sm text-zinc-500 uppercase tracking-wider mt-1">Automatiserat</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link
            to="/inspiration"
            data-testid="gallery-link"
            className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 font-medium tracking-wide hover:bg-[#C5A028] transition-all duration-300"
          >
            Se Våra Projekt
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default LightingSection;
